import React from 'react';
import Timeline from '@mui/lab/Timeline';
import Paper from '@mui/material/Paper';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import { CustomInstanceItem } from 'components/admin/orders/recurring-orders/components/CustomInstanceItem';
import { HistoryItem } from 'components/admin/orders/recurring-orders/components/HistoryItem';
import { SeriesSettingsItem } from 'components/admin/orders/recurring-orders/components/SeriesSettingsItem';
import {
  UpcomingInstanceItem,
  UpcomingInstanceItemType,
} from 'components/admin/orders/recurring-orders/components/UpcomingInstanceItem';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import {
  TimelineActions,
  TimelinePoints,
  getRecurringOrder,
} from 'api/admin/orders/getRecurringOrder';
import styles from './individualRecurringOrder.module.scss';

export const IndividualRecurringOrder = () => {
  const { id } = useParams();

  const {
    data,
    isLoading,
    refetch: refetchRecurringOrderDetails,
    error,
  } = useQuery(
    ['recurringOrder', { id }],
    async () => {
      const { data } = await getRecurringOrder(id);
      return data;
    },
    {
      onError: (error: any) => {
        if (error?.response?.code >= 500) {
          Sentry.captureException(error);
        }
      },
      retry: false,
    },
  );

  const nextScheduledOrderIndex = data?.timeline.items.findLastIndex(
    (item) =>
      item.type === TimelinePoints.UPCOMING_INSTANCE &&
      item.action === TimelineActions.INSTANCE_SCHEDULED &&
      !item.data?.isSkipped,
  );

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <CentsLoader />
      </div>
    );
  }
  if ((error?.response?.status as number) >= 400 || (error?.response?.status as number) < 500) {
    return (
      <div className={styles.wrapper}>
        <h2>Recurring order is not found</h2>
      </div>
    );
  }

  if (!isLoading && !data?.details) {
    <div className={styles.wrapper}>
      <h2>Something went wrong</h2>
    </div>;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <h1>Recurring order: {id}</h1>
      </div>
      <div className={styles.content}>
        <Paper className={styles.description}>
          {Object.entries(data!.details).map(([sectionTitle, sectionData]) => {
            return (
              <div key={sectionTitle}>
                <h2>{sectionTitle}</h2>
                {sectionData.map(({ name, value, link, subItems }) => (
                  <>
                    <div key={name}>
                      <h3>{name}</h3>
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer">
                          {value}
                        </a>
                      ) : (
                        <p>{value}</p>
                      )}
                    </div>
                    {subItems?.map((item) => (
                      <div className={styles.subItemContainer}>
                        <div className={styles.subItem}>{item.name}</div>
                        <div className={styles.subItem}>{item.value}</div>
                      </div>
                    ))}
                  </>
                ))}
              </div>
            );
          })}
        </Paper>
        <Paper className={styles.timeline}>
          <h2>Timeline</h2>
          <Timeline className={styles.timelineContent}>
            {data!.timeline.items.map((item, index) => {
              const key = `${item.itemTimestamp} ${item.instanceWeek} ${index}`;
              if (item.type === TimelinePoints.CUSTOM_INSTANCE) {
                return <CustomInstanceItem key={key} item={item} />;
              }
              if (item.type === TimelinePoints.SERIES_SETTINGS) {
                return <SeriesSettingsItem key={key} item={item} />;
              }
              if (item.type === TimelinePoints.INSTANCE_HISTORY_ITEM) {
                return <HistoryItem key={key} item={item} />;
              }
              if (item.type === TimelinePoints.UPCOMING_INSTANCE) {
                return (
                  <UpcomingInstanceItem
                    key={key}
                    item={item as UpcomingInstanceItemType}
                    isOrderCreationAvailable={nextScheduledOrderIndex === index}
                    recurringOrderId={id}
                    refetchRecurringOrderDetails={refetchRecurringOrderDetails}
                  />
                );
              }
            })}
          </Timeline>
        </Paper>
      </div>
    </div>
  );
};
